import React, { useState, useEffect } from 'react';
import { BellIcon, PaperAirplaneIcon, XMarkIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { createAuthRequestInit } from '../../utils/auth';
import { API_BASE } from '../../config/api';

interface Notification {
  id: string;
  title: string;
  message: string;
  targetUsers: string[];
  targetType: 'all' | 'selected' | 'role';
  sentAt: string;
  status: 'sent' | 'pending' | 'failed';
  readCount?: number;
  totalRecipients?: number;
}

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
}

interface AutomaticNotificationSetting {
  id: string;
  action: string;
  description: string;
  enabled: boolean;
  notifyOnSuccess: boolean;
  notifyOnFailure: boolean;
}

const NotificationsPage: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterDate, setFilterDate] = useState<string>('all');
  const [savingAutoSettings, setSavingAutoSettings] = useState(false);
  const [autoSettingsDirty, setAutoSettingsDirty] = useState(false);
  const [autoSettingsMessage, setAutoSettingsMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  const [automaticNotifications, setAutomaticNotifications] = useState<AutomaticNotificationSetting[]>([
    {
      id: 'quick-sms',
      action: 'Send Quick SMS',
      description: 'Notify users when they send a quick SMS message',
      enabled: false,
      notifyOnSuccess: true,
      notifyOnFailure: true
    },
    {
      id: 'sms-campaign',
      action: 'Send SMS Campaign',
      description: 'Notify users when they send an SMS campaign',
      enabled: false,
      notifyOnSuccess: true,
      notifyOnFailure: true
    },
    {
      id: 'create-template',
      action: 'Create Template',
      description: 'Notify users when they create a new SMS template',
      enabled: false,
      notifyOnSuccess: true,
      notifyOnFailure: true
    },
    {
      id: 'create-group',
      action: 'Create Group',
      description: 'Notify users when they create a new contact group',
      enabled: false,
      notifyOnSuccess: true,
      notifyOnFailure: true
    },
    {
      id: 'create-contact',
      action: 'Create Contact',
      description: 'Notify users when they create a new contact',
      enabled: false,
      notifyOnSuccess: true,
      notifyOnFailure: true
    },
    {
      id: 'download-sms-log',
      action: 'Download SMS Log',
      description: 'Notify users when they download SMS logs',
      enabled: false,
      notifyOnSuccess: true,
      notifyOnFailure: true
    },
    {
      id: 'update-profile',
      action: 'Update Profile Settings',
      description: 'Notify users when they update their profile settings',
      enabled: false,
      notifyOnSuccess: true,
      notifyOnFailure: true
    },
    {
      id: 'import-contacts',
      action: 'Import Contacts',
      description: 'Notify users when they import contacts',
      enabled: false,
      notifyOnSuccess: true,
      notifyOnFailure: true
    },
    {
      id: 'export-contacts',
      action: 'Export Contacts',
      description: 'Notify users when they export contacts',
      enabled: false,
      notifyOnSuccess: true,
      notifyOnFailure: true
    },
    {
      id: 'create-broadcast',
      action: 'Create Broadcast',
      description: 'Notify users when they create a broadcast message',
      enabled: false,
      notifyOnSuccess: true,
      notifyOnFailure: true
    },
    {
      id: 'allocate-credits',
      action: 'Allocate Credits',
      description: 'Notify users when credits are allocated to their account',
      enabled: false,
      notifyOnSuccess: true,
      notifyOnFailure: true
    },
    {
      id: 'subscription-update',
      action: 'Subscription Update',
      description: 'Notify users when their subscription is updated',
      enabled: false,
      notifyOnSuccess: true,
      notifyOnFailure: true
    },
    {
      id: 'low-credits-alert',
      action: 'Low Credits Alert',
      description: 'Alert users when their SMS credits are running low. Automatic notifications will be sent when credits reach: 50, 40, 30, 20, 10, and 0 credits remaining. Users will be prompted to top up their account.',
      enabled: false,
      notifyOnSuccess: true,
      notifyOnFailure: false
    }
  ]);

  const [newNotification, setNewNotification] = useState({
    title: '',
    message: '',
    targetType: 'all' as 'all' | 'selected' | 'role',
    selectedUserIds: [] as string[],
    selectedRole: 'user'
  });

  // Load notifications
  useEffect(() => {
    loadNotifications();
    loadUsers();
    loadAutomaticNotificationSettings();
  }, []);

  const loadAutomaticNotificationSettings = async () => {
    try {
      const res = await fetch(`${API_BASE}/notifications/automatic-settings`, createAuthRequestInit());
      if (res.ok) {
        const json = await res.json();
        const settings = json.data?.settings || json.settings || [];
        if (settings.length > 0) {
      setAutomaticNotifications(settings);
      setAutoSettingsDirty(false);
      setAutoSettingsMessage(null);
        }
      }
    } catch (e: any) {
      console.error('Failed to load automatic notification settings:', e);
      // Use default settings if API fails
    }
  };

  const handleToggleAutomaticNotification = async (id: string) => {
    const updated = automaticNotifications.map(setting => 
      setting.id === id 
        ? { ...setting, enabled: !setting.enabled }
        : setting
    );
    setAutomaticNotifications(updated);
    setAutoSettingsDirty(true);
  };

  const handleToggleNotifyOnSuccess = async (id: string) => {
    const updated = automaticNotifications.map(setting => 
      setting.id === id 
        ? { ...setting, notifyOnSuccess: !setting.notifyOnSuccess }
        : setting
    );
    setAutomaticNotifications(updated);
    setAutoSettingsDirty(true);
  };

  const handleToggleNotifyOnFailure = async (id: string) => {
    const updated = automaticNotifications.map(setting => 
      setting.id === id 
        ? { ...setting, notifyOnFailure: !setting.notifyOnFailure }
        : setting
    );
    setAutomaticNotifications(updated);
    setAutoSettingsDirty(true);
  };

  const saveAutomaticNotificationSettings = async (settingsToSave?: AutomaticNotificationSetting[]) => {
    setSavingAutoSettings(true);
    setAutoSettingsMessage(null);
    try {
      const settings = settingsToSave || automaticNotifications;
      const res = await fetch(`${API_BASE}/notifications/automatic-settings`, createAuthRequestInit({
        method: 'POST',
        body: JSON.stringify({ settings })
      }, true));
      
      if (!res.ok) {
        throw new Error('Failed to save settings');
      }
      setAutoSettingsDirty(false);
      setAutoSettingsMessage({
        type: 'success',
        text: 'Automatic notification settings updated successfully.'
      });
    } catch (e: any) {
      console.error('Failed to save automatic notification settings:', e);
      // Revert on error
      await loadAutomaticNotificationSettings();
      setAutoSettingsMessage({
        type: 'error',
        text: e?.message || 'Failed to update automatic notification settings.'
      });
    } finally {
      setSavingAutoSettings(false);
    }
  };

  const loadNotifications = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/notifications`, createAuthRequestInit());
      if (!res.ok) throw new Error('Failed to load notifications');
      const json = await res.json();
      setNotifications(json.data?.notifications || json.notifications || []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load notifications');
      // For now, use mock data if API fails
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const res = await fetch(`${API_BASE}/users`, createAuthRequestInit());
      if (!res.ok) throw new Error('Failed to load users');
      const json = await res.json();
      setUsers(json.data?.users || json.users || []);
    } catch (e: any) {
      console.error('Failed to load users:', e);
      setUsers([]);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setNewNotification(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleUserSelection = (userId: string) => {
    setNewNotification(prev => {
      const isSelected = prev.selectedUserIds.includes(userId);
      return {
        ...prev,
        selectedUserIds: isSelected
          ? prev.selectedUserIds.filter(id => id !== userId)
          : [...prev.selectedUserIds, userId]
      };
    });
  };

  const handleSendNotification = async () => {
    if (!newNotification.title || !newNotification.message) {
      alert('Please fill in title and message');
      return;
    }

    if (newNotification.targetType === 'selected' && newNotification.selectedUserIds.length === 0) {
      alert('Please select at least one user');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload: any = {
        title: newNotification.title,
        message: newNotification.message,
        targetType: newNotification.targetType
      };

      if (newNotification.targetType === 'selected') {
        payload.userIds = newNotification.selectedUserIds;
      } else if (newNotification.targetType === 'role') {
        payload.role = newNotification.selectedRole;
      }

      console.log('Sending notification request:', { url: `${API_BASE}/notifications`, payload });

      const res = await fetch(`${API_BASE}/notifications`, createAuthRequestInit({
        method: 'POST',
        body: JSON.stringify(payload)
      }, true));

      console.log('Notification response status:', res.status);

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ message: 'Failed to send notification' }));
        console.error('Notification error:', errorData);
        throw new Error(errorData.message || `Failed to send notification (Status: ${res.status})`);
      }

      const responseData = await res.json();
      console.log('Notification sent successfully:', responseData);

      // Reset form
      setNewNotification({
        title: '',
        message: '',
        targetType: 'all',
        selectedUserIds: [],
        selectedRole: 'user'
      });
      setShowCreateForm(false);

      // Reload notifications
      await loadNotifications();
    } catch (e: any) {
      console.error('Error sending notification:', e);
      setError(e?.message || 'Failed to send notification. Please check the console for details.');
    } finally {
      setLoading(false);
    }
  };

  const filteredNotifications = notifications.filter(notif => {
    if (filterStatus !== 'all' && notif.status !== filterStatus) return false;
    if (filterDate !== 'all') {
      const notifDate = new Date(notif.sentAt);
      const now = new Date();
      if (filterDate === 'today') {
        return notifDate.toDateString() === now.toDateString();
      } else if (filterDate === 'week') {
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        return notifDate >= weekAgo;
      } else if (filterDate === 'month') {
        const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        return notifDate >= monthAgo;
      }
    }
    return true;
  });

  const getStatusBadge = (status: string) => {
    const colors = {
      sent: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      failed: 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Notifications Management</h1>
          <p className="mt-2 text-sm text-gray-600">Create and manage push notifications for users</p>
        </div>

        {/* Create Notification Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-bold text-gray-900">Create Notification</h2>
            {!showCreateForm ? (
              <button
                onClick={() => setShowCreateForm(true)}
                className="flex items-center bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#F4511E] transition-colors text-sm"
              >
                <BellIcon className="w-5 h-5 mr-2" />
                New Notification
              </button>
            ) : (
              <button
                onClick={() => {
                  setShowCreateForm(false);
                  setNewNotification({
                    title: '',
                    message: '',
                    targetType: 'all',
                    selectedUserIds: [],
                    selectedRole: 'user'
                  });
                }}
                className="flex items-center text-gray-600 hover:text-gray-800"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            )}
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {showCreateForm && (
            <div className="space-y-6 border-t pt-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notification Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={newNotification.title}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] text-sm"
                  placeholder="Enter notification title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="message"
                  value={newNotification.message}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] text-sm"
                  rows={4}
                  placeholder="Enter notification message"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Target Audience
                </label>
                <select
                  name="targetType"
                  value={newNotification.targetType}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] text-sm"
                >
                  <option value="all">All Users</option>
                  <option value="selected">Selected Users</option>
                  <option value="role">By Role</option>
                </select>
              </div>

              {newNotification.targetType === 'selected' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Users
                  </label>
                  <div className="border border-gray-300 rounded-md p-4 max-h-60 overflow-y-auto">
                    {users.length === 0 ? (
                      <p className="text-sm text-gray-500">No users available</p>
                    ) : (
                      <div className="space-y-2">
                        {users.map(user => (
                          <label key={user.id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                            <input
                              type="checkbox"
                              checked={newNotification.selectedUserIds.includes(user.id)}
                              onChange={() => handleUserSelection(user.id)}
                              className="rounded border-gray-300 text-[#FF5722] focus:ring-[#FF5722]"
                            />
                            <span className="text-sm text-gray-700">
                              {user.firstName} {user.lastName} ({user.email}) - {user.role}
                            </span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                  {newNotification.selectedUserIds.length > 0 && (
                    <p className="mt-2 text-xs text-gray-600">
                      {newNotification.selectedUserIds.length} user(s) selected
                    </p>
                  )}
                </div>
              )}

              {newNotification.targetType === 'role' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Role
                  </label>
                  <select
                    name="selectedRole"
                    value={newNotification.selectedRole}
                    onChange={handleInputChange}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] text-sm"
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                    <option value="super_admin">Super Admin</option>
                    <option value="manager">Manager</option>
                  </select>
                </div>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowCreateForm(false);
                    setNewNotification({
                      title: '',
                      message: '',
                      targetType: 'all',
                      selectedUserIds: [],
                      selectedRole: 'user'
                    });
                  }}
                  className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendNotification}
                  disabled={loading}
                  className="flex items-center bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#F4511E] transition-colors text-sm disabled:opacity-50"
                >
                  <PaperAirplaneIcon className="w-5 h-5 mr-2" />
                  {loading ? 'Sending...' : 'Send Notification'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Automatic Notifications Configuration Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex flex-col gap-4 mb-6 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-lg font-bold text-gray-900">Automatic Notifications Configuration</h2>
              <p className="mt-2 text-sm text-gray-600">
                Configure automatic notifications that will be pushed to users when they execute specific tasks on the platform. 
                Notifications will confirm task completion status (success or failure) and indicate what the user initiated and what the platform executed.
              </p>
              {autoSettingsMessage && (
                <div
                  className={`mt-3 px-4 py-2 rounded-md text-sm ${
                    autoSettingsMessage.type === 'success'
                      ? 'bg-green-50 text-green-700 border border-green-200'
                      : 'bg-red-50 text-red-700 border border-red-200'
                  }`}
                >
                  {autoSettingsMessage.text}
                </div>
              )}
            </div>
            <button
              onClick={() => saveAutomaticNotificationSettings()}
              disabled={!autoSettingsDirty || savingAutoSettings}
              className={`whitespace-nowrap inline-flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                !autoSettingsDirty || savingAutoSettings
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  : 'bg-[#FF5722] text-white hover:bg-[#F4511E]'
              }`}
            >
              {savingAutoSettings ? 'Saving...' : 'Save Notification Settings'}
            </button>
          </div>

          {savingAutoSettings && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-sm text-blue-600">Saving settings...</p>
            </div>
          )}

          <div className="space-y-4">
            {automaticNotifications.map((setting) => (
              <div 
                key={setting.id}
                className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-sm font-semibold text-gray-900">{setting.action}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        setting.enabled 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {setting.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 mb-3">{setting.description}</p>
                    
                    {setting.enabled && (
                      <div className="mt-3 space-y-2 pl-4 border-l-2 border-gray-200">
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={setting.notifyOnSuccess}
                            onChange={() => handleToggleNotifyOnSuccess(setting.id)}
                            className="rounded border-gray-300 text-[#FF5722] focus:ring-[#FF5722]"
                          />
                          <span className="text-xs text-gray-700">Notify on successful completion</span>
                        </label>
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={setting.notifyOnFailure}
                            onChange={() => handleToggleNotifyOnFailure(setting.id)}
                            className="rounded border-gray-300 text-[#FF5722] focus:ring-[#FF5722]"
                          />
                          <span className="text-xs text-gray-700">Notify on failure</span>
                        </label>
                      </div>
                    )}
                  </div>
                  
                  <div className="ml-4 flex-shrink-0">
                    <button
                      onClick={() => handleToggleAutomaticNotification(setting.id)}
                      className={`
                        relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent 
                        transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:ring-offset-2
                        ${setting.enabled ? 'bg-[#FF5722]' : 'bg-gray-200'}
                      `}
                      role="switch"
                      aria-checked={setting.enabled}
                    >
                      <span
                        className={`
                          pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 
                          transition duration-200 ease-in-out
                          ${setting.enabled ? 'translate-x-5' : 'translate-x-0'}
                        `}
                      />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-900">
                  How Automatic Notifications Work
                </h3>
                <p className="mt-1 text-xs text-blue-700">
                  When enabled, users will receive push notifications automatically after executing the specified actions. 
                  The notification will include:
                </p>
                <ul className="mt-2 text-xs text-blue-700 list-disc list-inside space-y-1">
                  <li>Confirmation of what action the user initiated</li>
                  <li>Status of the action (success or failure)</li>
                  <li>Details of what the platform executed</li>
                  <li>Timestamp of when the action was completed</li>
                </ul>
                <div className="mt-3 pt-3 border-t border-blue-200">
                  <p className="text-xs font-medium text-blue-900 mb-1">Low Credits Alert</p>
                  <p className="text-xs text-blue-700">
                    The Low Credits Alert notification is triggered automatically when a user's credit balance reaches specific thresholds (50, 40, 30, 20, 10, or 0 credits). 
                    This helps ensure users are aware of their credit status and can top up before running out. Notifications are sent once per threshold to avoid spam.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Notifications List */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-bold text-gray-900">Sent Notifications</h2>
            <div className="flex space-x-4">
              <select
                className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] text-sm"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="sent">Sent</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
              </select>
              <select
                className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] text-sm"
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
              </select>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {loading && notifications.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-sm text-gray-500">Loading notifications...</p>
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="text-center py-8">
              <BellIcon className="w-12 h-12 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No notifications found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Message</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Target</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sent At</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recipients</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredNotifications.map((notif) => (
                    <tr key={notif.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 text-sm font-medium text-gray-900">{notif.title}</td>
                      <td className="px-4 py-4 text-sm text-gray-700 max-w-md truncate" title={notif.message}>
                        {notif.message}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600">
                        {notif.targetType === 'all' ? 'All Users' : 
                         notif.targetType === 'selected' ? `${notif.targetUsers.length} Selected` :
                         notif.targetType}
                      </td>
                      <td className="px-4 py-4 text-sm">
                        {getStatusBadge(notif.status)}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600">
                        {new Date(notif.sentAt).toLocaleString()}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600">
                        {notif.totalRecipients || notif.targetUsers.length || 0} sent
                        {notif.readCount !== undefined && ` • ${notif.readCount} read`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationsPage;

